import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../../../firebase-config";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@nextui-org/button";
import { Select, SelectItem } from "@nextui-org/select";
import { Input } from "@nextui-org/input";
import { Controller, useForm } from "react-hook-form";
import Image from "next/image";
import { ArrowBackIos, ErrorOutline, School } from "@material-ui/icons";
import { TbArrowsExchange } from "react-icons/tb";
import { MdDelete } from "react-icons/md";
import { Tooltip } from "@material-ui/core";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AssignmentIcon from "@material-ui/icons/Assignment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PustackButton from "../PustackButton";
import { ThemeContext } from "../../../context";
import { DatePicker } from "@nextui-org/date-picker";
import dayjs from "dayjs";
import {
  parseDate,
  getLocalTimeZone,
  CalendarDate,
  DateFormatter,
} from "@internationalized/date";

const formatter = new DateFormatter("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const BrandLogoLight =
  "https://firebasestorage.googleapis.com/v0/b/avian-display-193502.appspot.com/o/scholarship%2Fjeet_foundation_light.png?alt=media&token=d4257dc5-e5bc-49e9-b7ae-6fd1dceee778";
const BrandLogoDark =
  "https://firebasestorage.googleapis.com/v0/b/avian-display-193502.appspot.com/o/scholarship%2Fjeet_foundation_dark.png?alt=media&token=e3e80595-c74b-489b-a0b8-aae6fd88f72e";

const ScholarshipInput = ({
  value,
  onChange,
  invalidKeys,
  className,
  placeholder = "Type here...",
  name,
  type = "text",
  ...props
}) => {
  // function handleChange(e) {
  //   onTextChange(e.target.value);
  //   typeof onChange === 'function' && onChange(e);
  // }

  return (
    <div
      className={
        "scholarship-form-input " +
        (className ?? "") +
        (invalidKeys.includes(name) ? " error" : "")
      }
    >
      <input
        value={value}
        name={name}
        onChange={onChange}
        type={type}
        {...props}
      />
      <label htmlFor="">{placeholder}</label>
    </div>
  );
};

function formatPhoneNumber(value) {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) return value;

  // clean the input for any non-digit values.
  const phoneNumber = value.replace(/[^\d]/g, "");

  // phoneNumberLength is used to know when to apply our formatting for the phone number
  const phoneNumberLength = phoneNumber.length;

  // we need to return the value with no formatting if its less then four digits
  // this is to avoid weird behavior that occurs if you  format the area code to early

  if (phoneNumberLength < 6) return phoneNumber;

  // console.log('phone number - ', phoneNumber, `${phoneNumber.slice(0, 5)} ${phoneNumber.slice(5)}`);

  // if phoneNumberLength is greater than 4 and less the 7 we start to return
  // the formatted number
  // if (phoneNumberLength < 7) {
  return `${phoneNumber.slice(0, 5)} ${phoneNumber.slice(5, 10)}`;
  // }

  // finally, if the phoneNumberLength is greater then seven, we add the last
  // bit of formatting and return it.
  // return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
  //   3,
  //   6
  // )}-${phoneNumber.slice(6, 10)}`;
}

interface BaseFieldObject {
  label?: string;
  description?: string;
  field_type: string;
  is_optional: boolean;
}

interface InputFieldObject extends BaseFieldObject {
  field_type: "input-field";
  meta?: {
    input_type?: string;
  };
}

interface SelectFieldObject extends BaseFieldObject {
  field_type: "select-field";
  meta?: {
    options?: string[];
  };
}

interface AttachmentFieldObject extends BaseFieldObject {
  field_type: "attachment";
  meta?: {
    acceptable_types?: string[];
  };
}

interface DateFieldObject extends BaseFieldObject {
  field_type: "date-field";
  meta?: {
    iso_date?: string;
  };
}

interface LastThreeYearsMarksFieldObject extends BaseFieldObject {
  field_type: "last-three-years-marks";
}

type FieldObject =
  | DateFieldObject
  | InputFieldObject
  | SelectFieldObject
  | AttachmentFieldObject
  | LastThreeYearsMarksFieldObject;

const gradeMap = {
  "Class 12": ["Class 11", "Class 10", "Class 9"],
  "Class 11": ["Class 10", "Class 9", "Class 8"],
  "Class 10": ["Class 9", "Class 8", "Class 7"],
  "Class 9": ["Class 8", "Class 7", "Class 6"],
  "Class 8": ["Class 7", "Class 6", "Class 5"],
  "Class 7": ["Class 6", "Class 5", "Class 4"],
  "Class 6": ["Class 5", "Class 4", "Class 3"],
  "Class 5": ["Class 4", "Class 3", "Class 2"],
  "Class 4": ["Class 3", "Class 2", "Class 1"],
  "Class 3": ["Class 2", "Class 1", "Kindergarten"],
  "Class 2": ["Class 1", "Kindergarten", "Nursery"],
  "Class 1": ["Kindergarten", "Nursery", "Pre-Nursery"],
  Kindergarten: ["Nursery", "Pre-Nursery", "Playgroup"],
};

// function InputFieldForm({ field }: { field: InputFieldObject }) {
//   return (
//     <div>
//       <Input
//         type={field.meta?.input_type ?? "text"}
//         label={field.label ? field.label : "Field Label"}
//         classNames={{
//           inputWrapper: "rounded-[0.3em_0.3em_0_0] border-b border-b-gray-500",
//         }}
//       />
//       {field.description && (
//         <p className="text-gray-400 text-xs mt-2 font-light">
//           {field.description}
//         </p>
//       )}
//     </div>
//   );
// }

const blobUrlToFile = async (blobUrl, fileName) => {
  try {
    // Fetch the blob from the Blob URL
    const response = await fetch(blobUrl);
    const blob = await response.blob(); // Convert the response to a Blob

    // Create a File object from the Blob
    const file = new File([blob], fileName, {
      type: blob.type,
      lastModified: new Date().getTime(),
    });

    return file; // Return the File object
  } catch (error) {
    console.error("Error converting Blob URL to File:", error);
    throw error;
  }
};

const uploadAttachment = (file: File, folderPath: string) => {
  return new Promise((resolve, reject) => {
    // Step 1: Create a reference to where the file will be stored
    const storageRef = storage.ref();
    const fileRef = storageRef.child(`${folderPath}/${file.name}`);

    // Step 2: Upload the file
    const uploadTask = fileRef.put(file);

    // Step 3: Handle the upload progress and completion
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Track upload progress (if needed)
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log("Upload is " + progress + "% done");
      },
      (error) => {
        // Handle any errors during upload
        reject(error);
      },
      () => {
        // Step 4: Once the upload is complete, get the download URL
        uploadTask.snapshot.ref
          .getDownloadURL()
          .then((downloadUrl) => {
            console.log("File available at", downloadUrl);
            resolve(downloadUrl); // Return the download URL
          })
          .catch((error) => {
            reject(error);
          });
      }
    );
  });
};

function InputFieldForm({
  field,
  value,
  onChange,
  disabled,
}: {
  field: InputFieldObject;
  disabled: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div>
      <Input
        type={field.meta?.input_type ?? "text"}
        label={field.label ? field.label : "Field Label"}
        classNames={{
          inputWrapper: "rounded-[0.3em_0.3em_0_0] border-b border-b-gray-500",
        }}
        value={value} // Bind the value prop from react-hook-form
        onChange={onChange} // Trigger onChange when the input changes
        isDisabled={disabled}
      />
      {field.description && (
        <p className="text-gray-400 text-xs mt-2 font-light">
          {field.description}
        </p>
      )}
    </div>
  );
}

function SelectFieldForm({
  field,
  value,
  disabled,
  onChange,
}: {
  field: SelectFieldObject;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const fieldLabel = field.label ? field.label : "Field Label";

  return (
    <div>
      <Select
        label={fieldLabel}
        placeholder={`Select ${fieldLabel}`}
        classNames={{
          trigger: "rounded-[0.3em_0.3em_0_0] border-b border-b-gray-500",
          base: "!max-w-[unset] w-full",
          popoverContent: "rounded-[0_0_0.3em_0.3em] no-preflight",
          selectorIcon: "text-white",
        }}
        // value={value} // Bind the selected value to the controlled component
        // onChange={onChange} // Handle the change event
        selectedKeys={[value]} // Bind the selected value to the controlled component
        onSelectionChange={(selectedKeys) => {
          onChange(selectedKeys.anchorKey);
        }}
        isDisabled={disabled}
      >
        {field.meta?.options?.map((option) => (
          <SelectItem
            value={option}
            key={option}
            className="!rounded-none dark:text-white"
          >
            {option}
          </SelectItem>
        )) ?? []}
      </Select>
      {field.description && (
        <p className="text-gray-400 text-xs mt-2 font-light">
          {field.description}
        </p>
      )}
    </div>
  );
}

// function AttachmentFieldForm({ field }: { field: AttachmentFieldObject }) {
//   return (
//     <div>
//       <Button className="rounded-[0.3em] w-full bg-orange-600 px-0">
//         <label className="w-full cursor-pointer h-10 flex items-center justify-center">
//           <input
//             type="file"
//             accept={field.meta?.acceptable_types?.join(", ") ?? "*"}
//             hidden
//           />
//           <span className="text-white">
//             {field.label ? field.label : "Upload Attachment"}
//           </span>
//         </label>
//       </Button>
//       {field.description && (
//         <p className="text-gray-400 text-xs mt-2 font-light">
//           {field.description}
//         </p>
//       )}
//     </div>
//   );
// }

function AttachmentFieldForm({
  field,
  value,
  disabled,
  onChange,
}: {
  field: AttachmentFieldObject;
  disabled: boolean;
  value: { url: string | null; fileName: string | null };
  onChange: (data: { url: string | null; fileName: string | null }) => void;
}) {
  return (
    <div>
      {!value.url && (
        <Button className="rounded-[0.3em] w-full bg-orange-600 px-0">
          <label className="w-full cursor-pointer h-10 flex items-center justify-center">
            <input
              type="file"
              accept={field.meta?.acceptable_types?.join(", ") ?? "*"}
              hidden
              onChange={(e) => {
                // const files = e.target.files;
                // onChange(files && files.length > 0 ? files : null); // Update the react-hook-form state
                const files = e.target.files;
                if (files && files.length > 0) {
                  const file = files[0];
                  const fileUrl = URL.createObjectURL(file);
                  onChange({ url: fileUrl, fileName: file.name }); // Pass both Blob URL and file name
                } else {
                  onChange({ url: null, fileName: null });
                }
              }}
              disabled={disabled}
            />
            <span className="text-white">
              {field.label ? field.label : "Upload Attachment"}
            </span>
          </label>
        </Button>
      )}
      {value.url && (
        <div className="text-sm px-4 rounded-[0.3em] w-full bg-green-600 h-10 flex items-center justify-center">
          {/* <input
            type="file"
            accept={field.meta?.acceptable_types?.join(", ") ?? "*"}
            hidden
            onChange={(e) => {
              const files = e.target.files;
              onChange(files && files.length > 0 ? files : null); // Update the react-hook-form state
            }}
          /> */}
          <span className="text-white truncate overflow-hidden">
            {value?.fileName ?? "File Selected"}
          </span>
          {!disabled && (
            <div className="flex items-center gap-3 ml-6">
              <Tooltip title="Delete Attachment">
                <div
                  onClick={() => {
                    onChange(null);
                  }}
                >
                  <span className="text-lg text-red-500 cursor-pointer">
                    <MdDelete />
                  </span>
                </div>
              </Tooltip>
            </div>
          )}
        </div>
      )}
      {field.description && (
        <p className="text-gray-400 text-xs mt-2 font-light">
          {field.description}
        </p>
      )}
    </div>
  );
}

function DateFieldForm({
  field,
  value,
  onChange,
  disabled,
}: {
  field: DateFieldObject;
  disabled: boolean;
  value: string;
  onChange: (iso_date?: string) => void;
}) {
  const [date, setDate] = useState<CalendarDate | null>(null);

  useEffect(() => {
    if (value) {
      try {
        const [day, month, year] = value.split("-");
        const isoDate = `${year}-${month}-${day}`;
        setDate(parseDate(isoDate));
      } catch (e) {
        console.error("Parse Date Test - ", e);
      }
    }
  }, [value]);

  return (
    <div>
      <div

      // style={{
      //   // @ts-ignore
      //   "--nextui-default-100": "240 3.7% 10%",
      //   "--nextui-default-600": "240 4.88% 65%",
      // }}
      >
        <DatePicker
          className="custom-date-picker"
          classNames={{
            label: "!text-default-600",
          }}
          showMonthAndYearPickers
          label={field.label ? field.label : "Field Label"}
          isDisabled={disabled}
          value={date ? date : undefined}
          onChange={(date) => {
            setDate(date);
            // onChange(date.toString());
            // Extract the day, month, and year
            const day = String(date.day).padStart(2, "0");
            const month = String(date.month).padStart(2, "0");
            const year = date.year;

            onChange(`${day}-${month}-${year}`);
          }}
        />
      </div>
      {field.description && (
        <p className="text-gray-400 text-xs mt-2 font-light">
          {field.description}
        </p>
      )}
    </div>
  );
}

export default function ScholarshipForm({
  scholarshipRef,
  setActiveIndex,
  setOpenCriteriaModal,
  logo,
}: any) {
  const [formFields, setFormFields] = useState([]);
  const [pastGrades, setPastGrades] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [isDark] = useContext(ThemeContext).theme;

  useEffect(() => {
    db.collection("admin")
      .doc("scholarship_form")
      .get()
      .then((snapshot) => {
        const data = snapshot.data();
        setFormFields(data?.fields ?? []);
      });
  }, []);

  // Step 1: Create dynamic yup validation schema based on fields
  const getValidationSchema = (fields) => {
    const schema = fields.reduce((acc, field) => {
      let validator: any = yup.string();
      if (!field.is_optional) {
        validator = validator.required(`${field.label} is required`);
      }

      if (
        field.field_type === "input-field" &&
        field.meta?.input_type === "email"
      ) {
        validator = validator.email("Please enter a valid email");
      }

      if (
        field.field_type === "input-field" &&
        field.meta?.input_type === "number"
      ) {
        validator = yup
          .number()
          .typeError("Please enter a valid number")
          .required(`${field.label} is required`);
      }

      // Validation for file uploads (attachments)
      if (field.field_type === "attachment") {
        // Check if the attachment has a valid Blob URL and fileName (indicating a file was selected)
        validator = yup.object().shape({
          url: yup
            .string()
            .nullable() // Allow null values explicitly
            .required(`Please upload your ${field.label.toLowerCase()}`), // Custom error message
          fileName: yup
            .string()
            .nullable()
            .required("File name cannot be empty"), // Custom error message for the file name
        });
      }

      if (field.field_type === "last-three-years-marks" && pastGrades?.length) {
        validator = yup.object().shape(
          pastGrades.reduce((acc, grade) => {
            let validator: any = yup.number();
            if (!field.is_optional) {
              validator = validator.required(`Marks for ${grade} is required`);
            }
            acc[grade] = validator.typeError(
              `Marks for ${grade} must be a number`
            );
            return acc;
          }, {})
        );
      }

      acc[field.label] = validator;
      return acc;
    }, {});

    // // Add validation for the last three years' marks
    // if (pastGrades?.length) {
    //   schema["Last Three Years Marks"] = yup.object().shape(
    //     pastGrades.reduce((acc, grade) => {
    //       acc[grade] = yup
    //         .number()
    //         .typeError(`Marks for ${grade} must be a number`)
    //         .required(`Marks for ${grade} is required`);
    //       return acc;
    //     }, {})
    //   );
    // }

    return yup.object().shape(schema);
  };

  // Step 2: Create validation schema from form fields
  const validationSchema = getValidationSchema(formFields);

  // Step 3: Setup react-hook-form
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    watch,
    reset,
    resetField,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const selectedGrade = watch("Grade");

  const shouldFormBeDisabled = scholarshipRef.current.UNDER_REVIEW || isPending;

  useEffect(() => {
    if (selectedGrade) {
      setPastGrades(gradeMap[selectedGrade]);

      if (
        formFields.find((field) => field.label === "Last Three Years Marks") &&
        !shouldFormBeDisabled
      ) {
        // Clear previous marks
        resetField("Last Three Years Marks"); // Reset the entire lastThreeYearsMarks field

        // Set default marks for the new grades (optional)
        gradeMap[selectedGrade]?.forEach((grade) => {
          setValue(`${"Last Three Years Marks"}.${grade}`, "");
        });
      }
    }
  }, [selectedGrade, setValue, resetField, formFields, shouldFormBeDisabled]);

  useEffect(() => {
    const data = scholarshipRef.current.lastApplication;
    // Pre-fill the form when the component is mounted or data changes
    if (data && scholarshipRef.current.UNDER_REVIEW) {
      // Use reset to populate the entire form with existing data
      reset(data);

      // If you want to set specific fields, you can use setValue
      // setValue('First Name', existingFormData['First Name']);
      // setValue('Email', existingFormData['Email']);
    }
  }, [reset, scholarshipRef]);

  // Step 4: Handle form submission
  const onSubmit = async (_data) => {
    console.log("Form data submitted:", _data);
    let data = _data;
    let isThere = Object.hasOwnProperty.call(_data, "lastThreeYearsMarks");
    if (isThere) {
      delete data["lastThreeYearsMarks"];
    }

    try {
      setIsPending(true);

      const firestoreObject = {};

      // Iterate over the form data dynamically
      for (const fieldLabel in data) {
        const fieldData = data[fieldLabel];

        // Check if the field is an attachment (with a Blob URL)
        if (fieldData && typeof fieldData === "object" && fieldData.url) {
          const { url, fileName } = fieldData;

          // Convert Blob URL back to File
          const file = await blobUrlToFile(url, fileName);

          // Upload the file to Firebase Storage and get the download URL
          const downloadUrl = await uploadAttachment(file, "attachments");

          // Add the download URL to the Firestore object
          firestoreObject[fieldLabel] = {
            fileName,
            url: downloadUrl,
          };
        } else {
          // Handle regular fields (text, number, email, etc.)
          firestoreObject[fieldLabel] = fieldData;
        }
      }

      // Now you can store `firestoreObject` in Firestore
      console.log("Prepared Firestore Object:", firestoreObject);
      await scholarshipRef.current.apply(firestoreObject as any);
      setActiveIndex(3);
      setIsPending(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      setIsPending(false);
    }
  };

  const onError = (error) => {
    console.log("Error - ", error);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="scholarship-form-wrapper !justify-start flex flex-col overflow-hidden"
    >
      <div>
        {/* <div className="scholarship-form-header text-[13px]">
          <ArrowBackIos
            style={{ cursor: "pointer" }}
            onClick={() => {
              // handleClose();
              setActiveIndex(scholarshipRef.current.UNDER_REVIEW ? 0 : 1);
            }}
          />
          <h2>Scholarship</h2>
        </div> */}
        <div className="scholarship-form-header">
          <ArrowBackIcon
            style={{ cursor: "pointer" }}
            onClick={() => setActiveIndex(0)}
          />
          <h2>Scholarship</h2>
        </div>
        <div className="scholarship-form-logo flex justify-center !my-6">
          <Image width={100} height={100} src={logo} alt="" />
        </div>
      </div>
      <div className="flex flex-col gap-6 md:px-10 overflow-auto flex-1">
        {formFields.map((field) => {
          if (field.field_type === "input-field") {
            return (
              <Controller
                key={field.label}
                name={field.label}
                control={control}
                render={({ field: controllerField }) => (
                  <div key={field.label}>
                    <InputFieldForm
                      field={field}
                      value={controllerField.value}
                      onChange={controllerField.onChange}
                      disabled={shouldFormBeDisabled}
                    />
                    {errors[field.label] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors[field.label]?.message as any}
                      </p>
                    )}
                  </div>
                )}
              />
            );
          }

          if (field.field_type === "select-field") {
            return (
              <div key={field.label}>
                <Controller
                  name={field.label}
                  control={control}
                  render={({ field: controllerField }) => (
                    <SelectFieldForm
                      field={field}
                      value={controllerField.value}
                      onChange={(value) => controllerField.onChange(value)}
                      disabled={shouldFormBeDisabled}
                    />
                  )}
                />
                {errors[field.label] && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[field.label]?.message as any}
                  </p>
                )}
              </div>
            );
          }

          if (field.field_type === "attachment") {
            return (
              <div key={field.label}>
                <Controller
                  name={field.label}
                  control={control}
                  render={({ field: controllerField }) => (
                    <AttachmentFieldForm
                      field={field}
                      disabled={shouldFormBeDisabled}
                      value={
                        controllerField.value || { url: null, fileName: null }
                      } // Provide default value for controlled input
                      onChange={(data) => controllerField.onChange(data)} // Handle both Blob URL and file name
                    />
                  )}
                />
                {errors[field.label] && (
                  <p className="text-red-500 text-xs mt-1">
                    {/* @ts-ignore */}
                    {errors[field.label]?.url?.message ||
                      // @ts-ignore
                      errors[field.label]?.fileName?.message}
                  </p>
                )}
              </div>
            );
          }

          if (field.field_type === "date-field") {
            return (
              <div key={field.label}>
                <Controller
                  name={field.label}
                  control={control}
                  render={({ field: controllerField }) => {
                    console.log("controllerField - ", controllerField);
                    return (
                      <DateFieldForm
                        field={field}
                        value={controllerField.value}
                        onChange={(iso_date) =>
                          controllerField.onChange(iso_date)
                        }
                        disabled={shouldFormBeDisabled}
                      />
                    );
                  }}
                />
                {errors[field.label] && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[field.label]?.message as any}
                  </p>
                )}
              </div>
            );
          }

          if (
            field.field_type === "last-three-years-marks" &&
            pastGrades?.length
          ) {
            return (
              <div key="lastThreeYearsMarks">
                <h3
                  className={
                    "text-sm mb-1 text-opacity-50 " +
                    (shouldFormBeDisabled
                      ? " text-default-600"
                      : " text-black dark:text-white")
                  }
                >
                  Last 3 Years Marks
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {pastGrades?.map((grade, index) => (
                    <Controller
                      key={grade}
                      name={`${"Last Three Years Marks"}.${grade}`}
                      control={control}
                      render={({ field: controllerField }) => (
                        <div key={grade}>
                          <Input
                            type={"text"}
                            label={`${grade} Marks`}
                            classNames={{
                              inputWrapper:
                                "rounded-[0.3em_0.3em_0_0] border-b border-b-gray-500",
                            }}
                            value={controllerField.value}
                            onChange={controllerField.onChange}
                            isDisabled={shouldFormBeDisabled}
                          />
                          {errors?.["Last Three Years Marks"]?.[grade] && (
                            <p className="text-red-500 text-xs mt-1">
                              {
                                errors?.["Last Three Years Marks"]?.[grade]
                                  ?.message
                              }
                            </p>
                          )}
                        </div>
                      )}
                    />
                  ))}
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>

      {scholarshipRef.current.ABANDONED ? (
        <div className="scholarship-form-control span-full">
          <div className="scholarship-form-control-notice">
            <ErrorOutline />
            <span>
              Please go over the{" "}
              <span
                style={{ textDecoration: "underline" }}
                onClick={() => setOpenCriteriaModal(true)}
              >
                criteria
              </span>{" "}
              for the scholarship.
            </span>
          </div>
          <Button
            isLoading={isPending}
            type="submit"
            className="bg-blue-500 text-white rounded"
            isDisabled={isPending}
          >
            Submit Form
          </Button>
        </div>
      ) : (
        <div className="scholarship-form-control span-full">
          <div className="scholarship-form-control-notice success">
            <CheckCircleIcon />
            <span>You have already submitted the application</span>
          </div>
          <PustackButton
            className={"under-review"}
            isLoading={scholarshipRef.current.submitting}
            onClick={() => {}}
            disabled
          >
            <AssignmentIcon />
            <span>Application Under Review</span>
          </PustackButton>
        </div>
      )}
    </form>
  );
}
